import React, { useState, useRef } from "react";
import { api, track } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function ContactForm({ defaultType = "devis", title, description }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", service: "", zone: "", type: defaultType });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const openedAt = useRef(Date.now());
  const hpWebsite = useRef("");
  const hpEmail2 = useRef("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Nom et téléphone requis"); return;
    }
    // Validate French phone format client-side too
    const phoneClean = form.phone.replace(/\s/g, "");
    if (!/^(?:\+33|0)[\s.-]?[1-9](?:[\s.-]?\d{2}){4}$/.test(phoneClean)) {
      toast.error("Numéro de téléphone invalide (format FR attendu)"); return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        hp_website: hpWebsite.current,
        hp_email2: hpEmail2.current,
        ts_open: openedAt.current,
        referrer: document.referrer || "",
        page: window.location.pathname,
      });
      await track("devis_submit", { meta: { service: form.service, type: form.type } });
      setSent(true);
      toast.success("Votre demande a bien été envoyée — nous vous recontactons sous 24h.");
      setForm({ name: "", phone: "", email: "", message: "", service: "", zone: "", type: defaultType });
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="p-10 rounded-3xl bg-emerald-50 border border-emerald-200 text-center" data-testid="contact-form-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4"/>
        <h3 className="font-display text-2xl font-bold text-emerald-900 mb-2">Demande reçue !</h3>
        <p className="text-emerald-700 mb-6">Nous vous contactons sous 24h ouvrées.</p>
        <Button onClick={() => setSent(false)} variant="outline" className="rounded-full">Envoyer une autre demande</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 bg-white p-6 md:p-8 rounded-3xl shadow-soft border border-[hsl(var(--border))]" data-testid="contact-form">
      {/* Honeypot fields — visually hidden but not display:none (bots ignore display:none). Bots fill ANY visible input. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
        <label>Site internet<input type="text" name="website" tabIndex="-1" autoComplete="off" onChange={(e) => { hpWebsite.current = e.target.value; }} /></label>
        <label>Confirmez votre email<input type="email" name="email_confirmation" tabIndex="-1" autoComplete="off" onChange={(e) => { hpEmail2.current = e.target.value; }} /></label>
      </div>
      {title && <div>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-[color:var(--brand-navy)]">{title}</h3>
        {description && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{description}</p>}
      </div>}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider">Nom complet *</Label>
          <Input id="name" data-testid="input-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required className="mt-1.5 h-11 rounded-xl"/>
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider">Téléphone *</Label>
          <Input id="phone" data-testid="input-phone" type="tel" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} required className="mt-1.5 h-11 rounded-xl"/>
        </div>
      </div>

      <div>
        <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
        <Input id="email" data-testid="input-email" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} className="mt-1.5 h-11 rounded-xl"/>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Type de demande</Label>
          <Select value={form.type} onValueChange={(v)=>setForm({...form, type:v})}>
            <SelectTrigger data-testid="select-type" className="mt-1.5 h-11 rounded-xl"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="devis">Devis gratuit</SelectItem>
              <SelectItem value="rappel">Demande de rappel</SelectItem>
              <SelectItem value="contact">Question / info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="service" className="text-xs font-semibold uppercase tracking-wider">Besoin</Label>
          <Select value={form.service} onValueChange={(v)=>setForm({...form, service:v})}>
            <SelectTrigger data-testid="select-service" className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="Choisir..."/></SelectTrigger>
            <SelectContent>
              <SelectItem value="Installation climatisation">Installation climatisation</SelectItem>
              <SelectItem value="Plomberie">Plomberie</SelectItem>
              <SelectItem value="Dépannage">Dépannage urgent</SelectItem>
              <SelectItem value="Entretien">Entretien / contrat</SelectItem>
              <SelectItem value="Autre">Autre</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="zone" className="text-xs font-semibold uppercase tracking-wider">Ville / Code postal</Label>
        <Input id="zone" data-testid="input-zone" value={form.zone} onChange={e=>setForm({...form, zone:e.target.value})} placeholder="ex: Salon-de-Provence 13300" className="mt-1.5 h-11 rounded-xl"/>
      </div>

      <div>
        <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-wider">Votre message</Label>
        <Textarea id="message" data-testid="input-message" value={form.message} onChange={e=>setForm({...form, message:e.target.value})} rows={4} className="mt-1.5 rounded-xl" placeholder="Décrivez brièvement votre projet ou votre problème..."/>
      </div>

      <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white font-bold text-base" data-testid="submit-contact-form">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Envoi...</> : "Envoyer ma demande"}
      </Button>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center uppercase tracking-wider flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3 h-3 text-emerald-500" />
        Formulaire protégé anti-spam · Réponse 24h · 100% gratuit
      </p>
    </form>
  );
}
